import type {
  TesterTarget,
  TesterTargetCheckResult,
} from "@xop/dashboard-shared";
import { DEFAULT_DIALOUT_CALLER_ID } from "@xop/dashboard-shared";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useCan } from "../../auth/permissions.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { EmptyState } from "../../components/ui/empty-state.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { toast } from "../../components/ui/use-toast.js";
import { ApiError } from "../../lib/api.js";
import { testerApi } from "./client.js";

const QK = ["tester", "targets"] as const;

interface FormState {
  name: string;
  api_base: string;
  service_email: string;
  account_api_key: string;
  service_password: string;
  webcast_api_key: string;
  dialout_caller_id: string;
}

const EMPTY: FormState = {
  name: "",
  api_base: "https://c1.conferweb.com",
  service_email: "",
  account_api_key: "",
  service_password: "",
  webcast_api_key: "",
  dialout_caller_id: DEFAULT_DIALOUT_CALLER_ID,
};

export function TesterServersPage(): JSX.Element {
  const canManage = useCan("tester:run");
  const qc = useQueryClient();
  const targetsQuery = useQuery({ queryKey: QK, queryFn: testerApi.listTargets });

  const [editing, setEditing] = useState<TesterTarget | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<TesterTarget | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: QK });

  const remove = useMutation({
    mutationFn: (id: number) => testerApi.deleteTarget(id),
    onSuccess: () => {
      invalidate();
      toast({ title: "Server removed" });
    },
  });

  const openAdd = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (t: TesterTarget) => {
    setEditing(t);
    setShowForm(true);
  };

  const targets = targetsQuery.data?.targets ?? [];

  return (
    <Box>
      <PageHeader
        title="Test servers"
        description="Register a server and its API keys once; the runner uses these to drive live capability tests."
        actions={
          canManage && !showForm ? (
            <Button variant="contained" onClick={openAdd}>
              Add server
            </Button>
          ) : undefined
        }
      />

      {showForm && canManage && (
        <TargetForm
          key={editing?.id ?? "new"}
          editing={editing}
          onDone={() => {
            setShowForm(false);
            setEditing(null);
            invalidate();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}

      {targetsQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : targets.length === 0 && !showForm ? (
        <EmptyState
          title="No servers configured"
          description={
            canManage
              ? "Add a server (e.g. c1.conferweb.com) with its account API key and service credentials to start testing."
              : "Ask an administrator to register a test server."
          }
        />
      ) : (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {targets.map((t) => (
            <TargetCard
              key={t.id}
              target={t}
              canManage={canManage}
              onEdit={() => openEdit(t)}
              onDelete={() => setConfirmDelete(t)}
            />
          ))}
        </Stack>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Remove this server?"
        description={
          confirmDelete
            ? `“${confirmDelete.name}” and its stored keys will be deleted. Past run history is kept.`
            : ""
        }
        confirmLabel="Remove"
        onConfirm={async () => {
          if (confirmDelete) await remove.mutateAsync(confirmDelete.id);
          setConfirmDelete(null);
        }}
      />
    </Box>
  );
}

function TargetCard({
  target,
  canManage,
  onEdit,
  onDelete,
}: {
  target: TesterTarget;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<TesterTargetCheckResult | null>(null);

  const runCheck = async () => {
    setChecking(true);
    setResult(null);
    try {
      setResult(await testerApi.checkTarget(target.id));
    } catch (err) {
      setResult({
        ok: false,
        login_ok: false,
        feature_ok: null,
        message:
          err instanceof ApiError ? err.message : "Connectivity check failed.",
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: 18 }}>
            {target.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {target.api_base} · moderator {target.service_email || "—"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={
                target.has_account_api_key
                  ? "Account key set"
                  : "No account key"
              }
              color={target.has_account_api_key ? "success" : "default"}
              variant="outlined"
            />
            <Chip
              size="small"
              label={
                target.has_service_password ? "Password set" : "No password"
              }
              color={target.has_service_password ? "success" : "default"}
              variant="outlined"
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Caller ID ${target.dialout_caller_id}`}
            />
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={runCheck}
            disabled={checking}
          >
            {checking ? "Checking…" : "Test connection"}
          </Button>
          {canManage && (
            <>
              <Button size="small" onClick={onEdit}>
                Edit
              </Button>
              <Button size="small" color="error" onClick={onDelete}>
                Remove
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {result && (
        <Alert
          severity={result.ok ? "success" : "error"}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          {result.message}
        </Alert>
      )}
    </Card>
  );
}

function TargetForm({
  editing,
  onDone,
  onCancel,
}: {
  editing: TesterTarget | null;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [form, setForm] = useState<FormState>(
    editing
      ? {
          ...EMPTY,
          name: editing.name,
          api_base: editing.api_base,
          service_email: editing.service_email,
          dialout_caller_id: editing.dialout_caller_id,
          // Secrets are write-only — left blank means "keep existing".
          account_api_key: "",
          service_password: "",
          webcast_api_key: "",
        }
      : EMPTY,
  );

  const set = (k: keyof FormState) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return testerApi.updateTarget(editing.id, {
          name: form.name,
          api_base: form.api_base,
          service_email: form.service_email,
          dialout_caller_id: form.dialout_caller_id,
          // Only send secrets the operator actually typed.
          ...(form.account_api_key ? { account_api_key: form.account_api_key } : {}),
          ...(form.service_password ? { service_password: form.service_password } : {}),
          ...(form.webcast_api_key ? { webcast_api_key: form.webcast_api_key } : {}),
        });
      }
      return testerApi.createTarget({
        name: form.name,
        api_base: form.api_base,
        account_api_key: form.account_api_key,
        service_email: form.service_email,
        service_password: form.service_password,
        ...(form.webcast_api_key ? { webcast_api_key: form.webcast_api_key } : {}),
        dialout_caller_id: form.dialout_caller_id,
      });
    },
    onSuccess: () => {
      toast({ title: editing ? "Server updated" : "Server added" });
      onDone();
    },
    onError: (err) => {
      toast({
        title: "Couldn’t save",
        description: err instanceof ApiError ? err.message : "Unexpected error.",
        variant: "destructive",
      });
    },
  });

  const secretPlaceholder = editing ? "•••••• (leave blank to keep)" : "";
  const valid =
    form.name.trim() &&
    form.api_base.trim() &&
    form.service_email.trim() &&
    (editing || (form.account_api_key && form.service_password));

  return (
    <Card variant="outlined" sx={{ p: 3, mt: 2, mb: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {editing ? `Edit ${editing.name}` : "Add a test server"}
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Name"
          value={form.name}
          onChange={set("name")}
          fullWidth
          required
        />
        <TextField
          label="Server URL"
          value={form.api_base}
          onChange={set("api_base")}
          fullWidth
          required
          helperText="e.g. https://c1.conferweb.com"
        />
        <TextField
          label="Service account email / login"
          value={form.service_email}
          onChange={set("service_email")}
          fullWidth
          required
          autoComplete="off"
          helperText="The moderator that owns created notifications (e.g. xcrm)."
        />
        <TextField
          label="Account API key"
          value={form.account_api_key}
          onChange={set("account_api_key")}
          fullWidth
          required={!editing}
          type="password"
          autoComplete="new-password"
          placeholder={secretPlaceholder}
          helperText="Account-Manager-Api-Key app gate."
        />
        <TextField
          label="Service account password"
          value={form.service_password}
          onChange={set("service_password")}
          fullWidth
          required={!editing}
          type="password"
          autoComplete="new-password"
          placeholder={secretPlaceholder}
        />
        <TextField
          label="Webcast API key (optional)"
          value={form.webcast_api_key}
          onChange={set("webcast_api_key")}
          fullWidth
          type="password"
          autoComplete="new-password"
          placeholder={secretPlaceholder || "Uses the public default if blank"}
        />
        <TextField
          label="Dial-out caller ID"
          value={form.dialout_caller_id}
          onChange={set("dialout_caller_id")}
          fullWidth
          helperText="The number the carrier presents on call-out. Must be a valid DID or calls won’t ring."
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!valid || save.isPending}
        >
          {save.isPending ? "Saving…" : editing ? "Save changes" : "Add server"}
        </Button>
      </Stack>
    </Card>
  );
}
